// ============================================================================
// PollMethodsCatalog — СПИСОК МЕТОДИК с поиском и категориями (единый вид).
//
// Чисто презентационный контейнер: данные приходят готовыми (pollMethods.ts),
// действия карточек задаёт вызывающий экран. Никаких таблиц и модальных окон —
// сетка карточек, на телефоне одна колонка. Явные состояния: загрузка, ошибка
// (+ «Повторить»), пустой список, «ничего не найдено по поиску/категории»,
// частичный сбой чтения шаблонов (предупреждение, а не пустота).
// ============================================================================

import { useMemo, useState, type ReactNode } from 'react';
import { Loader2, RefreshCw, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  METHOD_CATEGORY_LABEL, METHOD_CATEGORY_ORDER, countPollMethods, filterPollMethods,
  type PollMethodCategory, type PollMethodItem,
} from '@/components/polls/pollMethods';
import { PollMethodCard } from '@/components/polls/PollMethodCard';

export interface PollMethodsCatalogProps {
  items: PollMethodItem[];
  loading?: boolean;
  error?: string;
  warnings?: string[];
  onRetry?: () => void;
  onOpen?: (method: PollMethodItem) => void;
  renderBadges?: (method: PollMethodItem) => ReactNode;
  renderActions?: (method: PollMethodItem) => ReactNode;
  selectedCode?: string;
  emptyTitle?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  headerExtra?: ReactNode;
}

export function PollMethodsCatalog({
  items, loading, error, warnings, onRetry, onOpen, renderBadges, renderActions,
  selectedCode, emptyTitle, emptyText, searchPlaceholder, headerExtra,
}: PollMethodsCatalogProps) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<PollMethodCategory | 'all'>('all');

  const counts = useMemo(() => countPollMethods(items), [items]);
  const visible = useMemo(() => filterPollMethods(items, search, category), [items, search, category]);
  const query = search.trim();
  const categories = useMemo(
    () => METHOD_CATEGORY_ORDER.filter((c) => (counts.byCategory[c] || 0) > 0),
    [counts],
  );

  const resetFilters = () => {
    setSearch('');
    setCategory('all');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-2 px-3 py-2">
        {headerExtra}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder || 'Поиск методики: название, код, описание…'}
            className="h-9 pl-8 pr-8 text-xs"
            aria-label="Поиск методики"
          />
          {search && (
            <button
              type="button"
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground hover:text-foreground"
              onClick={() => setSearch('')}
              title="Очистить поиск"
              aria-label="Очистить поиск"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <CategoryChip
            active={category === 'all'}
            label="Все"
            count={counts.all}
            onClick={() => setCategory('all')}
          />
          {categories.map((c) => (
            <CategoryChip
              key={c}
              active={category === c}
              label={METHOD_CATEGORY_LABEL[c]}
              count={counts.byCategory[c] || 0}
              onClick={() => setCategory(c)}
            />
          ))}
          {onRetry && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto h-7 shrink-0 px-2 text-[11px]"
              onClick={onRetry}
              disabled={loading}
              title="Обновить список методик"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            </Button>
          )}
        </div>

        {!loading && !error && items.length > 0 && (
          <div className="text-[11px] text-muted-foreground">
            {query || category !== 'all'
              ? `Найдено: ${visible.length} из ${items.length}`
              : `Методик: ${items.length}`}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-3 pb-3">
        {loading && items.length === 0 && (
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Загрузка методик…
          </div>
        )}

        {error && (
          <div className="space-y-1.5 rounded-lg border border-destructive/40 px-3 py-2 text-xs">
            <div className="text-sm font-medium text-destructive">Не удалось загрузить методики</div>
            <div className="text-muted-foreground break-words">{error}</div>
            {onRetry && (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onRetry}>
                Повторить
              </Button>
            )}
          </div>
        )}

        {!error && warnings && warnings.length > 0 && (
          <div className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px]">
            <div className="font-medium">Часть методик прочитана не полностью</div>
            <div className="text-muted-foreground break-words">
              Не удалось определить число вопросов: {warnings.join(', ')}. Методики доступны — откройте
              их, чтобы проверить шаблон.
            </div>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="space-y-1 rounded-lg border bg-card px-3 py-3 text-xs">
            <div className="text-sm font-medium">{emptyTitle || 'Методик нет'}</div>
            <div className="text-muted-foreground">
              {emptyText || 'Установите поставку с методиками (например, «ГидраМедик: психология») — методики появятся в этом списке.'}
            </div>
          </div>
        )}

        {!error && items.length > 0 && visible.length === 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-3 text-xs">
            <span className="text-muted-foreground">
              {query ? `По запросу «${query}» ничего не найдено.` : 'В этой категории методик нет.'}
            </span>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={resetFilters}>
              Сбросить поиск
            </Button>
          </div>
        )}

        {visible.length > 0 && (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {visible.map((method) => (
              <PollMethodCard
                key={method.code}
                method={method}
                onOpen={onOpen}
                badges={renderBadges?.(method)}
                actions={renderActions?.(method)}
                selected={selectedCode === method.code}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryChip({ active, label, count, onClick }: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:bg-accent/40',
      )}
      aria-pressed={active}
    >
      {label}
      <span className="ml-1 opacity-70">{count}</span>
    </button>
  );
}
